import axios from 'axios';
import { getAccessToken } from './auth';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').trim();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and fix Content-Type on GET requests
api.interceptors.request.use(
  async (config) => {
    try {
      // Don't send Content-Type on requests without a body — causes Starlette body errors
      if (!config.data && config.method?.toLowerCase() === 'get') {
        delete config.headers['Content-Type'];
      }
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to get access token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only a signed-in session that has expired goes back to the login page. Pages that work without a
    // team login (sign-in itself, client portal, interview, yarn, signing and promo links) never redirect,
    // which is what stops the login page reloading itself in a loop.
    const path = window.location.pathname;
    const publicPage = /^\/(login|invite\/|reset-password|client(\/|$)|interview\/|yarn\/|sign\/|promo\/|invoice\/|quote\/)/.test(path);
    if (error.response?.status === 401 && error.config?.headers?.Authorization && !publicPage) {
      localStorage.removeItem('heyday_token');
      localStorage.removeItem('heyday_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// API endpoints
export const companyAPI = {
  updateMe: (data) => api.patch('/api/companies/me', data),
};

export const authAPI = {
  getMe: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout'),
};

export const projectsAPI = {
  list: () => api.get('/api/projects/'),
  create: (data) => api.post('/api/projects/', data),
  update: (id, data) => {
    const { pipeline_stages, ...rest } = data || {};
    const ops = [api.put(`/api/projects/${id}`, rest)];
    if (pipeline_stages) {
      ops.push(api.put(`/api/projects/${id}/stages`, { stages: pipeline_stages }));
    }
    return Promise.all(ops).then((results) => results[0]);
  },
  delete: (id) => api.post(`/api/projects/${id}/archive`),
  getReport: (id) => api.get(`/api/projects/${id}/report`),
};

export const contactsAPI = {
  list: (params) => api.get('/api/contacts/', { params }),
  create: (data) => api.post('/api/contacts/', data),
  get: (id) => api.get(`/api/contacts/${id}`),
  update: (id, data) => api.patch(`/api/contacts/${id}`, data),
  delete: (id) => api.delete(`/api/contacts/${id}`),
  deduplicate: () => api.post('/api/contacts/migrate/dedup-by-email'),
  resubscribe: (id, consentNote) => api.post(`/api/contacts/${id}/resubscribe`, { consent_note: consentNote }),
};

export const activitiesAPI = {
  getByContact: (contactId) => api.get(`/api/activities/contact/${contactId}`),
  getByAccount: (accountId) => api.get(`/api/activities/account/${accountId}`),
  getRecent: (params) => api.get('/api/activities/recent', { params }),
  create: (data) => api.post('/api/activities/', data),
};

export const pipelineAPI = {
  get: (params) => api.get('/api/pipeline/', { params }),
  moveAccount: (accountId, newStage) => api.post('/api/pipeline/move', { account_id: accountId, new_stage: newStage }),
  moveStage: (contactId, newStage) => api.post('/api/pipeline/move', { contact_id: contactId, new_stage: newStage }),
};

export const accountsAPI = {
  list: (params) => api.get('/api/accounts', { params }),
  create: (data) => api.post('/api/accounts', data),
  get: (id) => api.get(`/api/accounts/${id}`),
  update: (id, data) => api.patch(`/api/accounts/${id}`, data),
  delete: (id) => api.delete(`/api/accounts/${id}`),
  migrate: () => api.post('/api/accounts/migrate'),
};

export const emailTemplatesAPI = {
  list: () => api.get('/api/email-templates/'),
  create: (data) => api.post('/api/email-templates/', data),
  update: (id, data) => api.patch(`/api/email-templates/${id}`, data),
  delete: (id) => api.delete(`/api/email-templates/${id}`),
};

export const notificationsAPI = {
  list: () => api.get('/api/notifications/'),
  unreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id) => api.patch(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/read-all'),
};

export const promoAPI = {
  list: () => api.get('/api/promo/campaigns'),
  create: (data) => api.post('/api/promo/campaigns', data),
  get: (id) => api.get(`/api/promo/campaigns/${id}`),
  update: (id, data) => api.patch(`/api/promo/campaigns/${id}`, data),
  delete: (id) => api.delete(`/api/promo/campaigns/${id}`),
  send: (id, data) => api.post(`/api/promo/campaigns/${id}/send`, data),
  uploadArtwork: (formData) => api.post('/api/social/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Public (no auth) — called from the listen page
  getListenData: (token) => api.get(`/api/promo/listen/${token}`),
  logEvent: (token, event) => api.post('/api/promo/event', { token, event }),
};

export { API_BASE_URL };

export const reportAPI = {
  emailProject: (projectId, email, period) =>
    api.post(`/api/projects/${projectId}/report/email`, { email }, { params: { period } }),
  emailAggregate: (email, period) =>
    api.post('/api/projects/aggregate/report/email', { email }, { params: { period } }),
};

export const aiAPI = {
  translate: (data) => api.post('/api/ai/translate', data),
};

export const sequencesAPI = {
  list: (params) => api.get('/api/sequences/', { params }),
  create: (data) => api.post('/api/sequences/', data),
  update: (id, data) => api.patch(`/api/sequences/${id}`, data),
  delete: (id) => api.delete(`/api/sequences/${id}`),
  enroll: (sequenceId, contactIds) => api.post('/api/sequences/enroll', { sequence_id: sequenceId, contact_ids: contactIds }),
  getEnrollments: (sequenceId) => api.get(`/api/sequences/${sequenceId}/enrollments`),
  testEmail: (sequenceId, toEmail) => api.post(`/api/sequences/${sequenceId}/test-email`, { to_email: toEmail }),
};

export const socialAPI = {
  list: (params) => api.get('/api/social/', { params }),
  create: (data) => api.post('/api/social/', data),
  update: (id, data) => api.patch(`/api/social/${id}`, data),
  delete: (id) => api.delete(`/api/social/${id}`),
  duplicate: (id) => api.post(`/api/social/${id}/duplicate`),
  publish: (id) => api.post(`/api/social/${id}/publish`),
};

// REACT_APP_MEDIA_BASE_URL lets you point media URLs at a custom domain
// (e.g. https://api.heydayhq.com) independently of the API base URL.
// If not set it falls back to REACT_APP_BACKEND_URL so nothing breaks.
const MEDIA_BASE_URL = (
  process.env.REACT_APP_MEDIA_BASE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  'http://localhost:8000'
).trim().replace(/\/$/, ''); // strip whitespace and trailing slash

export const socialMediaAPI = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/social/media/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Returns a public URL using the configured media domain.
  // Set REACT_APP_MEDIA_BASE_URL in Vercel to your custom domain so
  // images embedded in emails use heydayhq.com instead of railway.app.
  serveUrl: (mediaId) => `${MEDIA_BASE_URL}/api/social/media/${mediaId}`,
  delete: (mediaId) => api.delete(`/api/social/media/${mediaId}`),
};

export const tasksAPI = {
  list: (params) => api.get('/api/tasks/', { params }),
  create: (data) => api.post('/api/tasks/', data),
  update: (id, data) => api.patch(`/api/tasks/${id}`, data),
  delete: (id) => api.delete(`/api/tasks/${id}`),
  // NEW: Progress tracking
  addProgress: (id, data) => api.post(`/api/tasks/${id}/progress`, data),
  getProgress: (id) => api.get(`/api/tasks/${id}/progress`),
  // NEW: Subtasks
  createSubtask: (id, data) => api.post(`/api/tasks/${id}/subtasks`, data),
  getSubtasks: (id) => api.get(`/api/tasks/${id}/subtasks`),
  updateSubtask: (taskId, subtaskId, data) => api.patch(`/api/tasks/${taskId}/subtasks/${subtaskId}`, data),
  deleteSubtask: (taskId, subtaskId) => api.delete(`/api/tasks/${taskId}/subtasks/${subtaskId}`),
};

// Named task lists (private unless shared) and notes (project, contact, or private notepad)
export const taskListsAPI = {
  list: () => api.get('/api/task-lists'),
  create: (data) => api.post('/api/task-lists', data),
  update: (id, data) => api.patch(`/api/task-lists/${id}`, data),
  remove: (id) => api.delete(`/api/task-lists/${id}`),
};

export const notesAPI = {
  list: (params) => api.get('/api/notes', { params }),
  create: (data) => api.post('/api/notes', data),
  update: (id, data) => api.patch(`/api/notes/${id}`, data),
  remove: (id) => api.delete(`/api/notes/${id}`),
  makeTasks: (id) => api.post(`/api/notes/${id}/tasks`),
};

export const schedulingAPI = {
  list: (params) => api.get('/api/scheduling/', { params }),
  create: (data) => api.post('/api/scheduling/', data),
  reschedule: (id, newTime) => api.patch(`/api/scheduling/${id}`, { new_time: newTime }),
  cancel: (id) => api.delete(`/api/scheduling/${id}`),
};

export const filesAPI = {
  listByContact: (contactId) => api.get(`/api/files/contact/${contactId}`),
  upload: (contactId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/files/contact/${contactId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (contactId, fileId) => api.delete(`/api/files/contact/${contactId}/file/${fileId}`),
  download: (contactId, fileId, filename) =>
    api.get(`/api/files/contact/${contactId}/file/${fileId}`, { responseType: 'blob' }).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }),
};

export const apolloAPI = {
  search: (params) => api.post('/api/apollo/search', params),
  searchCompanies: (params) => api.post('/api/apollo/companies', params),
  enrich: (apolloId) => api.post('/api/apollo/enrich', { apollo_id: apolloId }),
  import: (projectId, people) => api.post('/api/apollo/import', { project_id: projectId, people }),
};

export const documentsAPI = {
  list: (params) => api.get('/api/documents/', { params }),
  upload: (formData) => api.post('/api/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, data) => api.patch(`/api/documents/${id}`, data),
  delete: (id) => api.delete(`/api/documents/${id}`),
  getContent: (id) => api.get(`/api/documents/${id}/download`, { responseType: 'arraybuffer' }),
  saveTemplate: (id, fields) => api.patch(`/api/documents/${id}`, { is_template: true, template_fields: fields }),
  clearTemplate: (id) => api.patch(`/api/documents/${id}`, { is_template: false, template_fields: [] }),
  download: (id, filename) =>
    api.get(`/api/documents/${id}/download`, { responseType: 'blob' }).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    }),
};

export const qrCodesAPI = {
  list: (params) => api.get('/api/qrcodes', { params }), // params: { project_id? }
  create: (data) => api.post('/api/qrcodes', data),
  delete: (id) => api.delete(`/api/qrcodes/${id}`),
  scans: (id) => api.get(`/api/qrcodes/${id}/scans`),
  download: (id, format, label) => {
    api.get(`/api/qrcodes/${id}/download`, { params: { format }, responseType: 'blob' }).then((res) => {
      const ext = format === 'svg' ? 'svg' : 'png';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replace(/\s+/g, '_')}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  },
};

export const esignAPI = {
  // parties: [{ email, name?, role: 'signer'|'reviewer' }, ...] — up to 10
  // fields: each tagged with role: 'signer'|'reviewer'|'sender'|'prefill'
  // prefillValues: { parameter_name: value } for any 'prefill' role fields
  // executor: { email, name? } — who fills 'sender' fields and executes; omit to default to you
  // message: optional note included in the signing-request email
  send: (documentId, parties, fields = [], prefillValues = {}, executor = undefined, message = undefined) =>
    api.post('/api/esign/send', {
      document_id: documentId,
      parties,
      fields,
      prefill_values: prefillValues,
      executor,
      message,
    }),
  listSubmissions: (documentId) =>
    api.get('/api/esign/submissions', { params: documentId ? { document_id: documentId } : {} }),
  viewSubmission: (submissionId) =>
    api.get(`/api/esign/submissions/${submissionId}/view`),
  countersignView: (submissionId) =>
    api.get(`/api/esign/countersign/${submissionId}/view`),
  countersign: (submissionId, fieldValues) =>
    api.post(`/api/esign/countersign/${submissionId}`, { field_values: fieldValues }),
  view: (token) => api.get(`/api/esign/view/${token}`),
  sign: (token, body) => api.post(`/api/esign/sign/${token}`, body),
  // release people still pending whose signature isn't needed; the request then moves on
  notRequired: (submissionId, partyIds, note) =>
    api.post(`/api/esign/submissions/${submissionId}/not-required`, { party_ids: partyIds, note }),
  // { party_id, email? } or { executor: true, email? }
  resend: (submissionId, body) => api.post(`/api/esign/submissions/${submissionId}/resend`, body),
};
